import dayjs from 'dayjs'
import {
  AgxDatePicker,
  AgxTypography,
  AgxCheckbox,
  AgxColumn,
  AgxRow,
  AgxRadioGroup,
  AgxErrorLabel,
} from '@urbanx/agx.ui-components2'
import {
  ListingAgreementDetails,
  AgreementType,
} from '@urbanx/agx.campaigns.client'
import { useMemo } from 'react'

interface AgreementDetailsProps {
  id: string
  value: ListingAgreementDetails
  onValueChange: (value: { id: string; value: ListingAgreementDetails }) => void
}

const AgreementDetailsComponent = ({
  id,
  value,
  onValueChange,
}: AgreementDetailsProps) => {
  const agreementType = value.agreementType
  const continueToOpenListingOnExpiration =
    value.continueToOpenListingOnExpiration ?? false

  const startDate = useMemo(
    () => (value.startDate ? dayjs(value.startDate).toDate() : null),
    [value.startDate]
  )

  const endDate = useMemo(
    () => (value.endDate ? dayjs(value.endDate).toDate() : null),
    [value.endDate]
  )

  const update = (patch: Partial<ListingAgreementDetails>) => {
    const updated: ListingAgreementDetails = {
      ...value,
      ...patch,
    }
    onValueChange({ id, value: updated })
  }

  const defaultOptions = [
    { label: AgreementType.Exclusive, value: AgreementType.Exclusive },
    { label: AgreementType.Open, value: AgreementType.Open },
    { label: AgreementType.Sole, value: AgreementType.Sole },
  ]

  const handleAgreementTypeChange = (newType: string) => {
    const type = newType as AgreementType

    update({
      agreementType: type,
      endDate: type === AgreementType.Open ? undefined : value.endDate,
    })
  }

  const startDatePicker = (
    <AgxRow fill>
      <AgxColumn gap="spacing-md" fill>
        <AgxDatePicker
          id="agreementStartDate"
          label="Start Date"
          value={startDate ?? undefined}
          required
          stretch
          onChange={(d: Date | null) =>
            update({
              startDate: d ?? undefined,
            })
          }
          optionalText=""
        />
        {!value.startDate && (
          <AgxErrorLabel
            id="startDateError"
            errorText="The start date is required"
          />
        )}
      </AgxColumn>
    </AgxRow>
  )

  const endDatePicker = (
    <AgxRow fill>
      <AgxColumn gap="spacing-md" fill>
        <AgxDatePicker
          id="agreementEndDate"
          label="End Date"
          required
          optionalText=""
          stretch
          value={endDate ?? undefined}
          onChange={(d: Date | null) =>
            update({
              endDate: d ?? undefined,
            })
          }
        />

        {value.startDate &&
          value.endDate &&
          dayjs(value.endDate).diff(dayjs(value.startDate), 'day') < 0 && (
            <AgxErrorLabel
              id="invalidDates"
              errorText="The end date should be after start date"
            />
          )}

        {!value.endDate && agreementType !== AgreementType.Open && (
          <AgxErrorLabel
            id="endDateError"
            errorText="The end date is required"
          />
        )}
      </AgxColumn>
    </AgxRow>
  )

  const continueCheckbox = (
    <AgxRow fill>
      <AgxCheckbox
        id="continueOnExpiration"
        label="Continue as an open listing upon expiration"
        style="label"
        labelSize="small"
        checked={continueToOpenListingOnExpiration}
        onChange={(checked) =>
          update({ continueToOpenListingOnExpiration: checked })
        }
      />
    </AgxRow>
  )

  return (
    <AgxColumn id={`${id}-agreementType`} gap="spacing-xl" fill>
      <AgxColumn gap="spacing-lg" fill>
        <AgxTypography
          id="agreementType"
          type="label"
          size="medium"
          color="var(--text-primary)"
        >
          Agreement Type
        </AgxTypography>

        <AgxRadioGroup
          id="agreementTypeRadioGroup"
          layout="vertical"
          options={defaultOptions}
          value={agreementType ?? ''}
          onChange={handleAgreementTypeChange}
          subContent={
            <AgxColumn
              key="agreementSubContent"
              gap="spacing-xl"
              padding={{
                top: 'spacing-2xl',
                bottom: 'spacing-2xl',
                left: 'spacing-lg',
                right: 'spacing-lg',
              }}
              fill
              alignItems="flex-start"
            >
              {agreementType === AgreementType.Exclusive && (
                <>
                  {startDatePicker}
                  {endDatePicker}
                  {continueCheckbox}
                </>
              )}
              {agreementType === AgreementType.Open && <>{startDatePicker}</>}
              {agreementType === AgreementType.Sole && (
                <>
                  {startDatePicker}
                  {endDatePicker}
                  {continueCheckbox}
                </>
              )}
            </AgxColumn>
          }
        />
      </AgxColumn>
    </AgxColumn>
  )
}

export default AgreementDetailsComponent
